import React, { Component as C, Fragment } from "react"
import PropTypes from "prop-types"
import { omit } from "lodash"
import { isNil } from "ramda"

import classNames from "classnames"
import resetStyles from "@cirrus/legacy/app-components/reset/reset.less"
import styles from "./button.less"
import withSkinV2 from "@cirrus/legacy/app-common/alt/framework/withSkinV2.jsx"
import v from "../variables.less"
import { darken, lighten } from "@cirrus/legacy/app-common/alt/framework/skin/skinUtils.es6"
import Icon from "@cirrus/legacy/app-components/icons/icon/index.jsx"
import AlignIcon from "@cirrus/legacy/app-components/icons/alignIcon.jsx"

class Button extends C {
  static propTypes = {
    type: PropTypes.string,
    iconSpin: PropTypes.bool,
  }

  static defaultProps = {
    type: "default",
    iconSpin: false,
  }

  getSizeClass() {
    var sizes = {
      medium: styles.medium,
    }

    var size = sizes[this.props.size]

    return size
  }

  getTypeClass() {
    let { classes } = this.props.dynamicStyles

    var types = {
      default: classNames(styles.default, classes.default),
      success: classNames(styles.success, classes.success),
      error: classNames(styles.error, classes.error),
      "filled-success": classNames(styles.filledSuccess, classes.filledSuccess),
      "filled-error": classNames(styles.filledError, classes.filledError),
      link: classNames(styles.link, classes.link),
    }

    var type = types[this.props.type]

    return type
  }

  getClass() {
    let { disabled, dynamicStyles } = this.props
    let { classes } = dynamicStyles
    return classNames(
      resetStyles.reset,
      styles.button,
      "rs_preserve",
      this.getTypeClass(),
      this.getSizeClass(),
      this.props.className,
      {
        [classes.disabled]: disabled,
        [styles.disabled]: disabled,
      },
    )
  }

  render() {
    const {
      icon: iconProp,
      iconSpin,
      iconClassName: iconClassNameProp,
      fixedIconSettings,
    } = this.props

    let icon = null
    if (!isNil(iconProp)) {
      if (!isNil(fixedIconSettings)) {
        icon = (
          <AlignIcon
            width={fixedIconSettings.fontSize}
            height={fixedIconSettings.fontSize}
            top={fixedIconSettings.top}
            facadeClassName={styles.iconLayout}>
            <Icon
              style={{ fontSize: fixedIconSettings.fontSize }}
              key="icon"
              icon={iconProp}
              spin={iconSpin}
              className={classNames(styles.icon, iconClassNameProp, {
                [styles.iconSpin]: iconSpin,
              })}
            />
          </AlignIcon>
        )
      } else {
        icon = (
          <Icon
            key="icon"
            icon={iconProp}
            spin={iconSpin}
            className={classNames(styles.iconLayout, styles.icon, iconClassNameProp, {
              [styles.iconSpin]: iconSpin,
            })}
          />
        )
      }
    }

    let content = (
      <Fragment>
        {icon}
        <span key="children">{this.props.children}</span>
      </Fragment>
    )

    let controlType = this.props.controlType || "button"
    let dataQa = this.props.dataQa || this.props["data-qa"]
    const dataTitle = this.props.dataTitle || this.props["data-title"]
    let buttonProps = omit(this.props, ["dataQa", "skinObject"]) //TODO: refactor to white list

    let props = {
      onClick: buttonProps.onClick,
      onFocus: buttonProps.onFocus,
      onBlur: buttonProps.onBlur,
      onMouseEnter: buttonProps.onMouseEnter,
      onMouseLeave: buttonProps.onMouseLeave,
      onMouseDown: buttonProps.onMouseDown,
      onMouseUp: buttonProps.onMouseUp,
      className: this.getClass(),
      disabled: buttonProps.disabled,
      tabIndex: buttonProps.tabIndex,
      autoFocus: buttonProps.autoFocus,
      href: buttonProps.href,
      target: buttonProps.target,
      title: buttonProps.title,
    }

    if (this.props.getButton) {
      props.ref = this.props.getButton
    }

    if (controlType === "button") {
      return (
        <button {...props} data-qa={dataQa} data-title={dataTitle} id={this.props.id}>
          {content}
        </button>
      )
    }

    if (controlType === "link") {
      return (
        <a {...props} data-qa={dataQa} data-title={dataTitle}>
          {content}
        </a>
      )
    }
  }
}

let buttonDefaultDarken3Color = darken(3, v.buttonDefaultColor)
let buttonDefaultLighten30Color = lighten(30, v.buttonDefaultColor)
let buttonDefault2Darken3Color = darken(3, v.buttonDefault2Color)
let buttonDefault2Lighten15Color = lighten(15, v.buttonDefault2Color)

let buttonSuccessDarken3Color = darken(3, v.buttonSuccessColor)
let buttonSuccessLighten15Color = lighten(15, v.buttonSuccessColor)

let buttonErrorDarken3Color = darken(3, v.buttonErrorColor)
let buttonErrorLighten15Color = lighten(15, v.buttonErrorColor)

let buttonLinkDarken15Color = darken(15, v.buttonLinkColor)
let buttonLinkLighten15Color = lighten(15, v.buttonLinkColor)

let defaultSkinObject = {
  default: {
    color: "",
    backgroundColor: "",
    borderColor: v.buttonDefault2Color,
    pm_focus: {
      color: buttonDefaultDarken3Color,
      backgroundColor: "",
      borderColor: buttonDefault2Darken3Color,
    },
    pm_hover: {
      color: buttonDefaultDarken3Color,
      backgroundColor: "",
      borderColor: v.buttonDefaultColor,
    },
    pm_active: {
      color: "",
      backgroundColor: "",
      borderColor: v.buttonDefaultColor,
      boxShadow: v.buttonBoxShadow,
    },
    m_disabled: {
      color: buttonDefaultLighten30Color,
      backgroundColor: "",
      borderColor: buttonDefault2Lighten15Color,
    },
  },
  success: {
    color: v.buttonSuccessColor,
    borderColor: v.buttonSuccessColor,
    backgroundColor: v.buttonFilledColor,
    pm_focus: {
      color: buttonSuccessDarken3Color,
      backgroundColor: "",
      borderColor: buttonSuccessDarken3Color,
    },
    pm_hover: {
      color: v.buttonFilledColor,
      backgroundColor: v.buttonSuccessColor,
      borderColor: "",
    },
    pm_active: {
      color: v.buttonFilledColor,
      backgroundColor: v.buttonSuccessColor,
      boxShadow: v.buttonBoxShadow,
      borderColor: "",
    },
    m_disabled: {
      color: v.buttonSuccessColor,
      borderColor: v.buttonSuccessColor,
    },
  },
  error: {
    color: v.buttonErrorColor,
    borderColor: v.buttonErrorColor,
    pm_focus: {
      color: buttonErrorDarken3Color,
      borderColor: buttonErrorDarken3Color,
    },
    pm_hover: {
      color: v.buttonFilledColor,
      backgroundColor: v.buttonErrorColor,
    },
    pm_active: {
      color: v.buttonFilledColor,
      backgroundColor: v.buttonErrorColor,
      boxShadow: v.buttonBoxShadow,
    },
    m_disabled: {
      color: buttonErrorLighten15Color,
      borderColor: buttonErrorLighten15Color,
    },
  },
  filledSuccess: {
    color: v.buttonFilledColor,
    backgroundColor: v.buttonSuccessColor,
    borderColor: v.buttonSuccessColor,
    pm_focus: {
      backgroundColor: buttonSuccessDarken3Color,
      borderColor: buttonSuccessDarken3Color,
    },
    pm_hover: {
      color: v.buttonFilledColor,
      backgroundColor: buttonSuccessDarken3Color,
      borderColor: buttonSuccessDarken3Color,
    },
    pm_active: {
      color: v.buttonFilledColor,
      backgroundColor: v.buttonSuccessColor,
      boxShadow: v.buttonBoxShadow,
    },
    m_disabled: {
      backgroundColor: v.buttonSuccessColor,
      borderColor: v.buttonSuccessColor,
    },
  },
  filledError: {
    color: v.buttonFilledColor,
    backgroundColor: v.buttonErrorColor,
    borderColor: v.buttonErrorColor,
    pm_focus: {
      backgroundColor: buttonErrorDarken3Color,
      borderColor: buttonErrorDarken3Color,
    },
    pm_hover: {
      color: v.buttonFilledColor,
      backgroundColor: buttonErrorDarken3Color,
      borderColor: buttonErrorDarken3Color,
    },
    pm_active: {
      color: v.buttonFilledColor,
      backgroundColor: v.buttonErrorColor,
      boxShadow: v.buttonBoxShadow,
    },
    m_disabled: {
      backgroundColor: buttonErrorLighten15Color,
      borderColor: buttonErrorLighten15Color,
    },
  },
  link: {
    color: v.buttonLinkColor,
    backgroundColor: v.buttonFilledColor,
    borderColor: "",
    pm_focus: {
      color: buttonLinkDarken15Color,
      backgroundColor: "",
      borderColor: "",
    },
    pm_hover: {
      color: buttonLinkDarken15Color,
      backgroundColor: "",
      borderColor: buttonLinkDarken15Color,
    },
    pm_active: {
      color: "",
      backgroundColor: "",
      boxShadow: v.buttonBoxShadow,
    },
    m_disabled: {
      color: buttonLinkLighten15Color,
    },
  },
}

export default withSkinV2({
  skinObjectSelector: (skinObject) => skinObject.button || {},
  hierarchySkinObjectSelector: (hierarchySkinObject) => {
    let buttonSuccessDarken3Color = darken(3, hierarchySkinObject.buttonsColor)
    let buttonSuccessLighten15Color = lighten(15, hierarchySkinObject.buttonsColor)
    return {
      filledSuccess: {
        backgroundColor: hierarchySkinObject.buttonsColor,
        pm_focus: {
          backgroundColor: buttonSuccessDarken3Color,
          borderColor: buttonSuccessLighten15Color,
        },
        pm_hover: {
          backgroundColor: buttonSuccessDarken3Color,
          borderColor: buttonSuccessLighten15Color,
        },
        pm_active: {
          backgroundColor: hierarchySkinObject.buttonsColor,
        },
        m_disabled: {
          backgroundColor: buttonSuccessLighten15Color,
          borderColor: buttonSuccessLighten15Color,
        },
      },
      success: {
        color: buttonSuccessLighten15Color,
        borderColor: hierarchySkinObject.buttonsColor,
        pm_focus: {
          color: v.buttonFilledColor,
          backgroundColor: buttonSuccessDarken3Color,
          borderColor: buttonSuccessLighten15Color,
        },
        pm_active: {
          backgroundColor: hierarchySkinObject.buttonsColor,
        },
        pm_hover: {
          backgroundColor: buttonSuccessDarken3Color,
          borderColor: buttonSuccessLighten15Color,
        },
      },
    }
  },
  defaultSkinObject: defaultSkinObject,
})(Button)
